import React from 'react'
import './distance-scheduler-fall-2025.css'
import axios from 'axios'
import { addDays, addWeeks, endOfWeek, format, startOfWeek, subWeeks, isSameDay, parse, differenceInYears, isValid } from 'date-fns'
import { MdChevronLeft, MdChevronRight, MdClose } from 'react-icons/md'
import { baseUrl } from '../../assets/Animations'
import { getRequestHeaders } from '../../api'
import { createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime } from '../../Calendar/calendarFunctions'
import TeacherDriveTimeBubble from '../../AdminWorkflows/TempTeacherDistancePopup/TeacherDriveTimeBubble/TeacherDriveTimeBubble'
import { createEllipses } from '../../utilityFunctions'

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const FALLBACK_RANGE = { start: 15 * 60, end: 20 * 60 } // default 3pm – 8pm

const createEmptyDayMap = () => {
    const map = {}
    DAY_NAMES.forEach((day) => {
        map[day] = []
    })
    return map
}

const createEmptyDateMap = (dates = []) => {
    const map = {}
    dates.forEach((date) => {
        map[format(date, 'yyyy-MM-dd')] = []
    })
    return map
}

const normalizeInstrument = (instrument) => {
    if (!instrument) return { key: null, label: null }
    const label = instrument.parentInstrument && instrument.parentInstrument !== 'Skill'
        ? instrument.parentInstrument
        : instrument.name
    const trimmed = label?.trim()
    return trimmed ? { key: trimmed.toLowerCase(), label: trimmed } : { key: null, label: null }
}

const normalizeStudentNameKey = (name) => {
    if (!name) return null
    return String(name).replace(/\s+/g, '').toLowerCase()
}

const STUDENT_DOB_FORMATS = ['yyyy-MM-dd', 'MM/dd/yyyy', 'M/d/yy', 'M/d/yyyy']

const isValidDateFormat = (value, formatString = 'yyyy-MM-dd') => {
    if (!value || typeof value !== 'string') return false
    const parsed = parse(value, formatString, new Date())
    return isValid(parsed)
}

const parseStudentDOB = (value) => {
    if (!value || typeof value !== 'string') return null
    const direct = new Date(value)
    if (!Number.isNaN(direct.getTime())) return direct
    const trimmed = value.trim()
    for (const pattern of STUDENT_DOB_FORMATS) {
        const parsed = parse(trimmed, pattern, new Date())
        if (isValid(parsed)) return parsed
    }
    return null
}

const getStudentAgeLabel = (student, familyStudent = null) => {
    const sources = [student, familyStudent].filter(Boolean)
    for (const source of sources) {
        const dobValue =
            source?.dob ||
            source?.studentDOB ||
            source?.studentDob ||
            source?.DOB ||
            source?.dateOfBirth ||
            source?.birthDate ||
            null
        const dobDate = parseStudentDOB(dobValue)
        if (dobDate) {
            const age = differenceInYears(new Date(), dobDate)
            if (Number.isFinite(age) && age >= 0) {
                return `${age} yrs`
            }
        }
        const ageValue =
            source?.age ??
            source?.Age ??
            source?.yearsOld ??
            source?.studentAge ??
            null
        if (ageValue != null) {
            const numeric = Number(ageValue)
            if (Number.isFinite(numeric) && numeric > 0) {
                return `${numeric} yrs`
            }
            if (typeof ageValue === 'string' && ageValue.trim()) {
                return ageValue.trim()
            }
        }
    }
    return null
}

const getLessonLengthLabel = (student, familyStudent = null) => {
    const sources = [student, familyStudent].filter(Boolean)
    for (const source of sources) {
        const lengthValue =
            source?.lessonLength ??
            source?.LessonLength ??
            source?.lesson_duration ??
            source?.lessonDuration ??
            source?.lesson?.lessonLength ??
            null
        if (lengthValue == null) continue
        const numeric = Number(lengthValue)
        if (Number.isFinite(numeric) && numeric > 0) {
            return `${numeric} min`
        }
        if (typeof lengthValue === 'string') {
            const trimmed = lengthValue.trim()
            if (!trimmed) continue
            if (trimmed.toLowerCase().includes('new student')) {
                continue
            }
            return trimmed
        }
    }
    return null
}

const getDriveSuggestionKey = (suggestion, fallbackIndex = 0) => {
    if (!suggestion) return `drive-suggestion-${fallbackIndex}`
    const obj = suggestion.eventOrTeacherObject || {}
    const directKey =
        suggestion.id ||
        suggestion.suggestionId ||
        obj.calendarEventId ||
        obj.eventId ||
        obj.id ||
        obj._id
    if (directKey) return String(directKey)
    const composite = [obj.teacherId || obj.id, obj.day, obj.time, obj.familyName]
        .filter(Boolean)
        .join('-')
    if (composite) return composite
    return `drive-suggestion-${fallbackIndex}`
}

const toMinutes = (value) => {
    if (!value || typeof value !== 'string') return null
    const [hours, minutes] = value.split(':').map((piece) => Number(piece))
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null
    return hours * 60 + minutes
}

const minutesToDate = (minutesValue) => {
    const base = new Date()
    base.setHours(0, 0, 0, 0)
    return new Date(base.getTime() + (Number(minutesValue) || 0) * 60000)
}

const minutesToDateOnDay = (dayDate, minutesValue) => {
    const base = new Date(dayDate)
    base.setHours(0, 0, 0, 0)
    return new Date(base.getTime() + (Number(minutesValue) || 0) * 60000)
}

const toUnixSeconds = (value) => {
    if (value == null) return null
    if (typeof value === 'number') {
        return value > 1e12 ? Math.round(value / 1000) : value
    }
    const date = value instanceof Date ? value : new Date(value)
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return null
    }
    return Math.round(date.getTime() / 1000)
}

const normalizeSlot = (slot) => {
    if (!slot) return null
    const startUnix = toUnixSeconds(
        slot.start ??
        slot.start_time ??
        slot.startTime ??
        slot.begin ??
        slot.startDate ??
        slot.start_date
    )
    const endUnix = toUnixSeconds(
        slot.end ??
        slot.end_time ??
        slot.endTime ??
        slot.finish ??
        slot.endDate ??
        slot.end_date
    )
    if (startUnix == null || endUnix == null || endUnix <= startUnix) return null
    return {
        startUnix,
        endUnix,
        durationMinutes: slot.durationMinutes || Math.max(1, Math.round((endUnix - startUnix) / 60))
    }
}

const normalizeSlots = (rawSlots) => {
    if (!Array.isArray(rawSlots)) return []
    return rawSlots
        .map((slot) => normalizeSlot(slot))
        .filter(Boolean)
        .sort((a, b) => a.startUnix - b.startUnix)
}

const mapSlotsToDayMinutes = (slots, weekDays = []) => {
    const map = createEmptyDateMap(weekDays)
    const allowedDateKeys = new Set(Object.keys(map))
    slots.forEach((slot) => {
        const startDate = new Date(slot.startUnix * 1000)
        const endDate = new Date(slot.endUnix * 1000)
        if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return
        const dateKey = format(startDate, 'yyyy-MM-dd')
        if (!allowedDateKeys.has(dateKey)) return
        const startMinutes = startDate.getHours() * 60 + startDate.getMinutes()
        const endMinutes = endDate.getHours() * 60 + endDate.getMinutes()
        if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes) || endMinutes <= startMinutes) return
        map[dateKey].push({ start: startMinutes, end: endMinutes })
    })
    Object.keys(map).forEach((key) => {
        map[key].sort((a, b) => a.start - b.start)
    })
    return map
}

const DistanceSchedulerFall2025 = ({ modalInfo, onClose, teacherDistanceSuggestions = [], familyProfile = null }) => {
    const modalRef = React.useRef(null)
    const todayWeekStart = React.useMemo(
        () => startOfWeek(new Date(), { weekStartsOn: 0 }),
        []
    )
    const defaultWeekStart = React.useMemo(
        () => startOfWeek(addWeeks(new Date(), 1), { weekStartsOn: 0 }),
        []
    )
    const [weekAnchor, setWeekAnchor] = React.useState(defaultWeekStart)
    const [teacherPool, setTeacherPool] = React.useState([])
    const [selectedTeacherId, setSelectedTeacherId] = React.useState(null)
    const [loadingTeachers, setLoadingTeachers] = React.useState(false)
    const [loadError, setLoadError] = React.useState('')
    const [calendarEvents, setCalendarEvents] = React.useState([])
    const [calendarLoading, setCalendarLoading] = React.useState(false)
    const [calendarError, setCalendarError] = React.useState('')
    const [calendarDaysWithEvents, setCalendarDaysWithEvents] = React.useState({})
    const [nylasAvailability, setNylasAvailability] = React.useState({
        teacherId: null,
        rangeKey: null,
        byDay: createEmptyDayMap()
    })
    const [nylasAvailabilityLoading, setNylasAvailabilityLoading] = React.useState(false)
    const [nylasAvailabilityError, setNylasAvailabilityError] = React.useState('')
    const nylasAvailabilityCacheRef = React.useRef({})
    const [hoveredLesson, setHoveredLesson] = React.useState(null)
    const [highlightedEvent, setHighlightedEvent] = React.useState(null)
    const [pendingHighlightSuggestion, setPendingHighlightSuggestion] = React.useState(null)
    const [activeDriveSuggestionKey, setActiveDriveSuggestionKey] = React.useState(null)
    const [driveFilterTeacherKey, setDriveFilterTeacherKey] = React.useState(null)
    const driveChipRefs = React.useRef(new Map())
    const studentList = modalInfo?.studentList || []
    const familyStudentMaps = React.useMemo(() => {
        const byId = new Map()
        const byName = new Map()
        const familyStudents = familyProfile?.students || []
        familyStudents.forEach((entry) => {
            const id = entry?.studentId || entry?.id
            if (id != null) {
                byId.set(String(id), entry)
            }
            const key = normalizeStudentNameKey(entry?.studentName || entry?.name)
            if (key) {
                byName.set(key, entry)
            }
        })
        return { byId, byName }
    }, [familyProfile])

    const getFamilyStudentMatch = React.useCallback(
        (student) => {
            if (!student) return null
            const { byId, byName } = familyStudentMaps
            if (student.studentId != null) {
                const match = byId.get(String(student.studentId))
                if (match) return match
            }
            const key = normalizeStudentNameKey(student.studentName || student.Name)
            if (key) {
                return byName.get(key) || null
            }
            return null
        },
        [familyStudentMaps]
    )
    const availabilityNote = React.useMemo(() => {
        const raw = familyProfile?.initialAvailabilityNotes
        if (!raw || typeof raw !== 'string') return ''
        if (isValidDateFormat(raw, 'yyyy-MM-dd')) {
            return 'An admin has changed family availability. See initial email for the original note.'
        }
        return raw.trim()
    }, [familyProfile?.initialAvailabilityNotes])

    const [showFullAvailabilityNote, setShowFullAvailabilityNote] = React.useState(false)
    React.useEffect(() => {
        setShowFullAvailabilityNote(false)
    }, [availabilityNote])

    const desiredLessonLength = React.useMemo(() => {
        const durations = studentList
            .map((student) => parseInt(student.lessonLength, 10))
            .filter((value) => Number.isFinite(value) && value > 0)
        return durations[0] || 30
    }, [studentList])
    const weekRangeKey = React.useMemo(
        () => format(weekAnchor, 'yyyy-MM-dd'),
        [weekAnchor]
    )
    const nylasCacheKey = React.useMemo(() => {
        if (!selectedTeacherId) return null
        return `${selectedTeacherId}:${weekRangeKey}:${desiredLessonLength}`
    }, [selectedTeacherId, weekRangeKey, desiredLessonLength])
    const weekDays = React.useMemo(() => (
        Array.from({ length: 7 }, (_, dayIndex) => addDays(weekAnchor, dayIndex))
    ), [weekAnchor])

const selectedTeacher = React.useMemo(
        () => teacherPool.find((teacher) => teacher.TeacherID === selectedTeacherId) || null,
        [teacherPool, selectedTeacherId]
    )

    const instrumentFilters = React.useMemo(() => {
        if (!modalInfo?.studentList?.length) return []
        const map = new Map()

        modalInfo.studentList.forEach((student) => {
            (student.instrumentList || []).forEach((instrument) => {
                const { key, label } = normalizeInstrument(instrument)
                if (key && !map.has(key)) {
                    map.set(key, label)
                }
            })
        })

        return Array.from(map.entries()).map(([key, label]) => ({ key, label }))
    }, [modalInfo?.studentList])

    const schoolFilter = React.useMemo(() => modalInfo?.whichSchool?.toLowerCase() || null, [modalInfo?.whichSchool])

    React.useEffect(() => {
        if (!schoolFilter || instrumentFilters.length === 0) {
            setTeacherPool([])
            setSelectedTeacherId(null)
            return
        }

        setLoadingTeachers(true)
        setLoadError('')

        axios.get(`${baseUrl}/initialadminrequest`, getRequestHeaders())
            .then((res) => {
                const teachers = Array.isArray(res.data) ? res.data : []
                const normalizedInstrumentKeys = instrumentFilters.map((inst) => inst.key)

                const filteredTeachers = teachers.filter((teacher) => {
                    const sameSchool = teacher?.School?.toLowerCase() === schoolFilter
                    const isActive = teacher?.Status?.toLowerCase() === 'active'
                    if (!sameSchool || !isActive) return false

                    const playsInstrument = Array.isArray(teacher?.InstrumentList) && teacher.InstrumentList.some((instrument) => {
                        const { key } = normalizeInstrument(instrument)
                        return key && normalizedInstrumentKeys.includes(key)
                    })

                    return playsInstrument
                })

                filteredTeachers.sort((a, b) => a.Name.localeCompare(b.Name))

                setTeacherPool(filteredTeachers)
                setSelectedTeacherId((prev) => {
                    if (prev && filteredTeachers.some((teach) => teach.TeacherID === prev)) {
                        return prev
                    }
                    return filteredTeachers[0]?.TeacherID || null
                })
            })
            .catch((err) => {
                console.error(err)
                setLoadError('We could not load teachers for this family.')
            })
            .finally(() => setLoadingTeachers(false))
    }, [instrumentFilters, schoolFilter])

    React.useEffect(() => {
        if (!selectedTeacherId) {
            setCalendarEvents([])
            return
        }

        setCalendarLoading(true)
        setCalendarError('')

        axios.get(`${baseUrl}/calendar/teacher/${selectedTeacherId}`, getRequestHeaders())
            .then((res) => {
                setCalendarEvents(Array.isArray(res.data) ? res.data : [])
            })
            .catch((err) => {
                console.error(err)
                setCalendarError('Calendar events are unavailable right now.')
                setCalendarEvents([])
            })
            .finally(() => setCalendarLoading(false))
    }, [selectedTeacherId])

    React.useEffect(() => {
        if (!calendarEvents.length) {
            setCalendarDaysWithEvents({})
            return
        }
        const displayedWeek = Array.from({ length: 7 }, (_, index) => addDays(weekAnchor, index))
        createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime(
            displayedWeek,
            calendarEvents,
            setCalendarDaysWithEvents
        )
    }, [calendarEvents, weekAnchor])

    const weekEventsByDay = React.useMemo(() => {
        const bucket = {}
        weekDays.forEach((date) => {
            const key = format(date, 'yyyy-MM-dd')
            const dayData = calendarDaysWithEvents[key]
            if (!dayData || !dayData.events?.length) return
            const transformed = dayData.events
                .filter((event) => !event.isPendingOffer)
                .map((event) => {
                    const startDate = new Date(event.dateTime)
                    const duration = Number(event.lessonLength) || 30
                    const startMinutes = startDate.getHours() * 60 + startDate.getMinutes()
                    const endMinutes = startMinutes + Math.max(duration, 15)
                    const endDate = new Date(startDate.getTime() + Math.max(duration, 15) * 60000)
                    return {
                        id: event.id || event._id || `${event.dateTime}-${event.studentId || Math.random()}`,
                        startMinutes,
                        endMinutes,
                        startDate,
                        endDate,
                        studentName: event.studentName,
                        familyName: event.familyName,
                        location: event.lessonLocation || event.familyAddress?.city || ''
                    }
                })
            if (transformed.length) {
                transformed.sort((a, b) => a.startMinutes - b.startMinutes)
                bucket[key] = transformed
            }
        })
        return bucket
    }, [calendarDaysWithEvents, weekDays])

    const baseAvailabilityByDay = React.useMemo(() => {
    if (!selectedTeacher) return createEmptyDayMap()
    const details = selectedTeacher.AvailabilityDetails || {}
    const normalized = {}

    DAY_NAMES.forEach((day) => {
        const slots = Array.isArray(details[day]) ? details[day] : []
        normalized[day] = slots
            .map((slot) => {
                const start = toMinutes(slot.start)
                const end = toMinutes(slot.end)
                if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null
                return { start, end }
            })
            .filter(Boolean)
    })

    return normalized
}, [selectedTeacher])

const weekAvailabilityWindow = React.useMemo(() => {
    let earliest = null
    let latest = null
    weekDays.forEach((date) => {
        const dayName = DAY_NAMES[date.getDay()]
        const slots = baseAvailabilityByDay[dayName] || []
        slots.forEach((slot) => {
            const startDate = minutesToDateOnDay(date, slot.start)
            const endDate = minutesToDateOnDay(date, slot.end)
            if (!earliest || startDate < earliest) earliest = startDate
            if (!latest || endDate > latest) latest = endDate
        })
    })
    if (!earliest || !latest || latest <= earliest) {
        return {
            start: weekAnchor,
            end: addDays(weekAnchor, 7)
        }
    }
    return { start: earliest, end: latest }
}, [weekDays, baseAvailabilityByDay, weekAnchor])

const usingNylasAvailability = React.useMemo(() => {
    if (!nylasCacheKey) return false
    if (nylasAvailability.teacherId !== selectedTeacherId) return false
    if (nylasAvailability.rangeKey !== nylasCacheKey) return false
    if (nylasAvailabilityError) return false
    const byDay = nylasAvailability.byDay || {}
    return Object.values(byDay).some((slots) => slots.length > 0)
}, [nylasCacheKey, nylasAvailability, selectedTeacherId, nylasAvailabilityError])

React.useEffect(() => {
        if (!nylasCacheKey) {
            setNylasAvailability({
                teacherId: null,
                rangeKey: null,
                byDay: createEmptyDateMap(weekDays)
            })
            setNylasAvailabilityError('')
            setNylasAvailabilityLoading(false)
            return
        }

        const cached = nylasAvailabilityCacheRef.current[nylasCacheKey]
        if (cached) {
            setNylasAvailability({
                teacherId: selectedTeacherId,
                rangeKey: nylasCacheKey,
                byDay: cached.byDay || createEmptyDayMap()
            })
            setNylasAvailabilityError(cached.error || '')
            setNylasAvailabilityLoading(false)
            return
        }

        let cancelled = false
        setNylasAvailabilityLoading(true)
        setNylasAvailabilityError('')
        setNylasAvailability({
            teacherId: selectedTeacherId,
            rangeKey: nylasCacheKey,
            byDay: createEmptyDateMap(weekDays)
        })

        const windowStart = weekAvailabilityWindow.start
        const windowEnd = weekAvailabilityWindow.end
        const rawTeacherRecordId =
            selectedTeacher?.MongoId ||
            selectedTeacher?._id ||
            selectedTeacher?.id ||
            null
        const teacherRecordId = rawTeacherRecordId
            ? rawTeacherRecordId.toString()
            : null
        const teacherIdentifier = teacherRecordId || selectedTeacherId
        const parsedTeacherId = Number(teacherIdentifier)
        const payload = {
            teacherId: Number.isFinite(parsedTeacherId) ? parsedTeacherId : teacherIdentifier,
            start: windowStart.toISOString(),
            end: windowEnd.toISOString(),
            durationMinutes: desiredLessonLength || 30,
            intervalMinutes: Math.min(
                30,
                Math.max(5, Math.round((desiredLessonLength || 30) / 2))
            )
        }
        if (teacherRecordId && teacherRecordId !== selectedTeacherId) {
            payload.teacherRecordId = teacherRecordId
        }
        payload.teacherLegacyId = selectedTeacherId
        const teacherEmail =
            selectedTeacher?.CalendarEmail ||
            selectedTeacher?.calendarEmail ||
            selectedTeacher?.calendarConnectionEmail ||
            selectedTeacher?.PersonalEmail ||
            selectedTeacher?.personalEmail ||
            selectedTeacher?.Email ||
            selectedTeacher?.email ||
            null
        if (teacherEmail) {
            payload.email = teacherEmail
        }

        axios
            .post(
                `${baseUrl}/api/integrations/nylas/availability`,
                payload,
                getRequestHeaders()
            )
            .then((response) => {
                if (cancelled) return
                const rawSlots = response?.data?.timeSlots || response?.data?.time_slots || []
                const normalizedSlots = normalizeSlots(rawSlots)
                const byDay = mapSlotsToDayMinutes(normalizedSlots, weekDays)
                nylasAvailabilityCacheRef.current[nylasCacheKey] = { byDay, error: '' }
                setNylasAvailability({
                    teacherId: selectedTeacherId,
                    rangeKey: nylasCacheKey,
                    byDay
                })
                setNylasAvailabilityError('')
            })
            .catch((error) => {
                if (cancelled) return
                const status = error?.response?.status
                const message =
                    error?.response?.data?.error ||
                    (status === 404
                        ? 'No connected personal calendar.'
                        : 'Unable to load Google Calendar availability.')
                nylasAvailabilityCacheRef.current[nylasCacheKey] = {
                    byDay: createEmptyDateMap(weekDays),
                    error: message
                }
                setNylasAvailability({
                    teacherId: selectedTeacherId,
                    rangeKey: nylasCacheKey,
                    byDay: createEmptyDateMap(weekDays)
                })
                setNylasAvailabilityError(message)
            })
            .finally(() => {
                if (!cancelled) {
                    setNylasAvailabilityLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [nylasCacheKey, selectedTeacherId, desiredLessonLength, weekAvailabilityWindow, selectedTeacher, weekDays])

    React.useEffect(() => {
        setHoveredLesson(null)
        setHighlightedEvent(null)
    }, [selectedTeacherId, weekAnchor])

    React.useEffect(() => {
        if (!activeDriveSuggestionKey) return
        if (!pendingHighlightSuggestion) {
            setActiveDriveSuggestionKey(null)
            return
        }
        const suggestionTeacherId = getSuggestionTeacherId(pendingHighlightSuggestion)
        if (
            suggestionTeacherId &&
            selectedTeacherId &&
            suggestionTeacherId !== selectedTeacherId
        ) {
            setActiveDriveSuggestionKey(null)
            setPendingHighlightSuggestion(null)
            setHighlightedEvent(null)
        }
    }, [activeDriveSuggestionKey, pendingHighlightSuggestion, selectedTeacherId])

    const formattedWeekLabel = React.useMemo(() => {
        const weekEnd = endOfWeek(weekAnchor, { weekStartsOn: 0 })
        const sameMonth = format(weekAnchor, 'MMM') === format(weekEnd, 'MMM')
        const startLabel = format(weekAnchor, sameMonth ? 'MMM d' : 'MMM d')
        const endLabel = format(weekEnd, sameMonth ? 'd' : 'MMM d')
        return `${startLabel} – ${endLabel}`
    }, [weekAnchor])

    const weekTitle = React.useMemo(() => {
        if (isSameDay(weekAnchor, todayWeekStart)) {
            return 'This Week'
        }
        if (isSameDay(weekAnchor, defaultWeekStart)) {
            return 'Next Week'
        }
        return `Week of ${format(weekAnchor, 'MMM d')}`
    }, [weekAnchor, todayWeekStart, defaultWeekStart])

    const nylasBusyByDay = React.useMemo(() => {
        if (!usingNylasAvailability) return createEmptyDateMap(weekDays)
        const result = createEmptyDateMap(weekDays)
        weekDays.forEach((date) => {
            const dayName = DAY_NAMES[date.getDay()]
            const dateKey = format(date, 'yyyy-MM-dd')
            const baseSlots = baseAvailabilityByDay[dayName] || []
            if (!baseSlots.length) return
            const availableSlots =
                (nylasAvailability.byDay && nylasAvailability.byDay[dateKey]) || []
            baseSlots.forEach((window) => {
                let cursor = window.start
                const overlaps = availableSlots
                    .map((slot) => ({
                        start: Math.max(slot.start, window.start),
                        end: Math.min(slot.end, window.end)
                    }))
                    .filter((slot) => slot.end > slot.start)
                    .sort((a, b) => a.start - b.start)
                overlaps.forEach((slot) => {
                    if (slot.start > cursor) {
                        result[dateKey].push({ start: cursor, end: slot.start })
                    }
                    cursor = Math.max(cursor, slot.end)
                })
                if (cursor < window.end) {
                    result[dateKey].push({ start: cursor, end: window.end })
                }
            })
        })
        return result
    }, [usingNylasAvailability, baseAvailabilityByDay, nylasAvailability, weekDays])

    const displayMinutesRange = React.useMemo(() => {
        let minStart = Infinity
        let maxEnd = -Infinity

        Object.values(baseAvailabilityByDay).forEach((slots = []) => {
            slots.forEach(({ start, end }) => {
                minStart = Math.min(minStart, start)
                maxEnd = Math.max(maxEnd, end)
            })
        })

        Object.values(weekEventsByDay).forEach((events = []) => {
            events.forEach(({ startMinutes, endMinutes }) => {
                minStart = Math.min(minStart, startMinutes)
                maxEnd = Math.max(maxEnd, endMinutes)
            })
        })

        if (!Number.isFinite(minStart) || !Number.isFinite(maxEnd) || maxEnd - minStart < 15) {
            return FALLBACK_RANGE
        }

        return {
            start: Math.max(0, minStart - 15),
            end: Math.min(24 * 60, maxEnd + 15)
        }
    }, [baseAvailabilityByDay, weekEventsByDay])

    const hourMarkers = React.useMemo(() => {
        const markers = []
        const totalRange = Math.max(displayMinutesRange.end - displayMinutesRange.start, 1)
        const startHour = Math.floor(displayMinutesRange.start / 60)
        const endHour = Math.ceil(displayMinutesRange.end / 60)
        for (let hour = startHour; hour <= endHour; hour++) {
            const minuteMark = hour * 60
            const clampedMinute = Math.max(
                displayMinutesRange.start,
                Math.min(displayMinutesRange.end, minuteMark)
            )
            const position = ((clampedMinute - displayMinutesRange.start) / totalRange) * 100
            markers.push({
                label: format(minutesToDate(minuteMark), 'h a'),
                position
            })
        }
        return markers
    }, [displayMinutesRange])

    const hideLessonTooltip = () => setHoveredLesson(null)

    const handleLessonHover = (lesson, target) => {
        if (!modalRef.current || !target) return
        const containerRect = modalRef.current.getBoundingClientRect()
        const targetRect = target.getBoundingClientRect()
        setHoveredLesson({
            lesson,
            top: targetRect.top - containerRect.top + targetRect.height / 2,
            left: targetRect.right - containerRect.left + 12
        })
    }

    const renderAvailabilityBlocks = (dayName, dateKey) => {
        if (!selectedTeacher) {
            return (
                <div className='distance-scheduler-2025__day-placeholder'>
                    Select a teacher to view availability
                </div>
            )
        }
        const slots = baseAvailabilityByDay[dayName] || []
        if (!slots.length) {
            return (
                <div className='distance-scheduler-2025__day-placeholder'>
                    No availability
                </div>
            )
        }

        const totalRange = Math.max(displayMinutesRange.end - displayMinutesRange.start, 1)

        return slots.map((slot, index) => {
            const clampedStart = Math.max(slot.start, displayMinutesRange.start)
            const clampedEnd = Math.min(slot.end, displayMinutesRange.end)
            if (clampedEnd <= displayMinutesRange.start || clampedStart >= displayMinutesRange.end) {
                return null
            }
            const top = ((clampedStart - displayMinutesRange.start) / totalRange) * 100
            const naturalBottom = ((displayMinutesRange.end - clampedEnd) / totalRange) * 100
            let bottom = Math.max(naturalBottom, 0)
            const minPercent = 4
            const currentHeight = 100 - top - bottom
            if (currentHeight < minPercent) {
                bottom = Math.max(0, 100 - top - minPercent)
            }
            const startLabel = format(minutesToDate(slot.start), 'h:mm a')
            const endLabel = format(minutesToDate(slot.end), 'h:mm a')
            const ariaLabel = `${dayName} availability ${startLabel} – ${endLabel}`

            return (
                <div
                    key={`${dayName}-avail-${index}-${slot.start}-${slot.end}`}
                    className='distance-scheduler-2025__slot-block'
                    style={{ top: `${top}%`, bottom: `${bottom}%` }}
                    aria-label={ariaLabel}
                    onMouseEnter={(event) =>
                        handleLessonHover(
                            {
                                studentLabel: `${selectedTeacherName || selectedTeacher?.Name || 'Teacher'} Availability`,
                                familyLabel: dayName,
                                timeLabel: `${startLabel} – ${endLabel}`
                            },
                            event.currentTarget
                        )
                    }
                    onMouseLeave={hideLessonTooltip}
                    onFocus={(event) =>
                        handleLessonHover(
                            {
                                studentLabel: `${selectedTeacherName || selectedTeacher?.Name || 'Teacher'} Availability`,
                                familyLabel: dayName,
                                timeLabel: `${startLabel} – ${endLabel}`
                            },
                            event.currentTarget
                        )
                    }
                    onBlur={hideLessonTooltip}
                    tabIndex={0}
                />
            )
        })
    }

    const renderBusyBlocks = (dayName, dateKey) => {
        if (!usingNylasAvailability) return null
        const slots = nylasBusyByDay[dateKey] || []
        if (!slots.length) return null

        const totalRange = Math.max(displayMinutesRange.end - displayMinutesRange.start, 1)

        return slots.map((slot, index) => {
            const clampedStart = Math.max(slot.start, displayMinutesRange.start)
            const clampedEnd = Math.min(slot.end, displayMinutesRange.end)
            if (clampedEnd <= displayMinutesRange.start || clampedStart >= displayMinutesRange.end) {
                return null
            }
            const top = ((clampedStart - displayMinutesRange.start) / totalRange) * 100
            const naturalBottom = ((displayMinutesRange.end - clampedEnd) / totalRange) * 100
            let bottom = Math.max(naturalBottom, 0)
            const minPercent = 6
            const currentHeight = 100 - top - bottom
            if (currentHeight < minPercent) {
                bottom = Math.max(0, 100 - top - minPercent)
            }
            const startLabel = format(minutesToDate(slot.start), 'h:mm a')
            const endLabel = format(minutesToDate(slot.end), 'h:mm a')
            const ariaLabel = `Google Calendar busy ${startLabel} – ${endLabel}`

            return (
                <div
                    key={`${dateKey}-busy-${index}-${slot.start}-${slot.end}`}
                    className='distance-scheduler-2025__slot-block distance-scheduler-2025__slot-block--busy'
                    style={{ top: `${top}%`, bottom: `${bottom}%` }}
                    aria-label={ariaLabel}
                    onMouseEnter={(event) =>
                        handleLessonHover(
                            {
                                studentLabel: `${selectedTeacherName || selectedTeacher?.Name || 'Teacher'} Busy`,
                                familyLabel: 'Google Calendar',
                                timeLabel: `${startLabel} – ${endLabel}`
                            },
                            event.currentTarget
                        )
                    }
                    onMouseLeave={hideLessonTooltip}
                    onFocus={(event) =>
                        handleLessonHover(
                            {
                                studentLabel: `${selectedTeacherName || selectedTeacher?.Name || 'Teacher'} Busy`,
                                familyLabel: 'Google Calendar',
                                timeLabel: `${startLabel} – ${endLabel}`
                            },
                            event.currentTarget
                        )
                    }
                    onBlur={hideLessonTooltip}
                    tabIndex={0}
                />
            )
        })
    }

    const renderLessonBlocks = (dateKey) => {
        const events = weekEventsByDay[dateKey] || []
        if (!events.length) return null
        const totalRange = Math.max(displayMinutesRange.end - displayMinutesRange.start, 1)

        return events.map((event) => {
            const clampedStart = Math.max(event.startMinutes, displayMinutesRange.start)
            const clampedEnd = Math.min(event.endMinutes, displayMinutesRange.end)
            if (clampedEnd <= displayMinutesRange.start || clampedStart >= displayMinutesRange.end) {
                return null
            }
            const top = ((clampedStart - displayMinutesRange.start) / totalRange) * 100
            const bottom = Math.max(((displayMinutesRange.end - clampedEnd) / totalRange) * 100, 0)
            const studentLabel = event.studentName || 'Lesson'
            const familyLabel = event.familyName || event.location || ''
            const timeLabel = `${format(event.startDate, 'h:mm a')} – ${format(event.endDate, 'h:mm a')}`
            const ariaLabel = `${studentLabel} · ${familyLabel} · ${timeLabel}`
            const isHighlighted =
                highlightedEvent &&
                highlightedEvent.dateKey === dateKey &&
                highlightedEvent.eventId === event.id
            const eventKey = `${dateKey}:${event.id}`
            const hasLinkedDrive = driveSuggestionEventMap.has(eventKey)

            const isSuggestionActive =
                pendingHighlightSuggestion &&
                driveSuggestionEventMap.get(`${dateKey}:${event.id}`)?.suggestion === pendingHighlightSuggestion

            return (
                <div
                    key={event.id}
                    className={`distance-scheduler-2025__event-block ${
                        isHighlighted ? 'is-highlighted' : ''
                    } ${hasLinkedDrive ? 'is-linkable' : ''
                    } ${isSuggestionActive ? 'has-suggestion' : ''}`}
                    style={{ top: `${top}%`, bottom: `${bottom}%` }}
                    aria-label={ariaLabel}
                    onMouseEnter={(domEvent) =>
                        handleLessonHover({ studentLabel, familyLabel, timeLabel }, domEvent.currentTarget)
                    }
                    onMouseLeave={hideLessonTooltip}
                    onClick={() => hasLinkedDrive && handleCalendarEventSelect(dateKey, event.id)}
                    onKeyDown={(eventKeyDown) => {
                        if (!hasLinkedDrive) return
                        if (eventKeyDown.key === 'Enter' || eventKeyDown.key === ' ') {
                            eventKeyDown.preventDefault()
                            handleCalendarEventSelect(dateKey, event.id)
                        }
                    }}
                    role={hasLinkedDrive ? 'button' : 'presentation'}
                    tabIndex={hasLinkedDrive ? 0 : -1}
                />
            )
        })
    }

    const moveWeek = (direction) => {
        setWeekAnchor((prev) => direction === 'next' ? addWeeks(prev, 1) : subWeeks(prev, 1))
    }

    const schoolLabel = modalInfo?.whichSchool?.toUpperCase() || 'School'
    const resolveImageUrl = (raw) => {
        if (!raw) return null
        return raw.startsWith('http') ? raw : `https://app.atharo.app/images/${raw}`
    }

    const getTeacherImage = (teacher) => resolveImageUrl(teacher?.imageUrl || teacher?.ImageName)

    const normalizeImageKey = (raw) => {
        if (!raw) return null
        const normalized = String(raw).trim().toLowerCase()
        if (!normalized) return null
        const parts = normalized.split(/[/\\]/).filter(Boolean)
        return (parts.pop() || normalized) || null
    }

    const getSuggestionImageKey = (suggestion) =>
        normalizeImageKey(suggestion?.eventOrTeacherObject?.imageUrl)

    const getTeacherImageKey = (teacher) => normalizeImageKey(teacher?.imageUrl || teacher?.ImageName)

    const buildFilterKey = (imageKey, id) => {
        if (imageKey && id) return `${imageKey}__${String(id).toLowerCase()}`
        if (imageKey) return imageKey
        if (id) return String(id).toLowerCase()
        return null
    }

    const getSuggestionFilterKey = (suggestion) =>
        buildFilterKey(getSuggestionImageKey(suggestion), getSuggestionTeacherId(suggestion))

    const getTeacherFilterKey = (teacher) =>
        buildFilterKey(getTeacherImageKey(teacher), teacher?.TeacherID)

    const driveTimeSuggestions = React.useMemo(() => {
        if (!Array.isArray(teacherDistanceSuggestions)) return []
        return [...teacherDistanceSuggestions].sort(
            (a, b) => (a.sortingDuration || Infinity) - (b.sortingDuration || Infinity)
        )
    }, [teacherDistanceSuggestions])

    const getSuggestionTeacherId = (suggestion) =>
        suggestion?.eventOrTeacherObject?.teacherId ||
        suggestion?.eventOrTeacherObject?.teacherID ||
        suggestion?.eventOrTeacherObject?.TeacherID ||
        suggestion?.eventOrTeacherObject?.id ||
        null

    const driveSuggestionEntries = React.useMemo(() => {
        const seen = new Set()
        const entries = []
        driveTimeSuggestions.forEach((suggestion, index) => {
            const key = getDriveSuggestionKey(suggestion, index)
            if (seen.has(key)) return
            seen.add(key)
            entries.push({ suggestion, key })
        })
        return entries
    }, [driveTimeSuggestions])

    const visibleDriveSuggestions = React.useMemo(() => {
        if (!driveFilterTeacherKey) return driveSuggestionEntries
        return driveSuggestionEntries.filter(({ suggestion }) => {
            const key = getSuggestionFilterKey(suggestion)
            return key && key === driveFilterTeacherKey
        })
    }, [driveSuggestionEntries, driveFilterTeacherKey])

    React.useEffect(() => {
        if (!driveFilterTeacherKey || !activeDriveSuggestionKey) return
        const entry = driveSuggestionEntries.find(({ key }) => key === activeDriveSuggestionKey)
        if (!entry) return
        const entryKey = getSuggestionFilterKey(entry.suggestion)
        if (entryKey && entryKey !== driveFilterTeacherKey) {
            setActiveDriveSuggestionKey(null)
            setPendingHighlightSuggestion(null)
            setHighlightedEvent(null)
        }
    }, [driveFilterTeacherKey, activeDriveSuggestionKey, driveSuggestionEntries])

    const parseSuggestionDate = (suggestion) => {
        const obj = suggestion?.eventOrTeacherObject || {}
        if (obj.dateTime) {
            const date = new Date(obj.dateTime)
            if (!isNaN(date)) return date
        }
        if (obj.startISO) {
            const date = new Date(obj.startISO)
            if (!isNaN(date)) return date
        }
        if (obj.startUnix) {
            const date = new Date(obj.startUnix * 1000)
            if (!isNaN(date)) return date
        }
        return null
    }

    const parseTimeStringToMinutes = (value) => {
        if (!value || typeof value !== 'string') return null
        const formats = ['h:mm a', 'h a', 'H:mm']
        for (const fmt of formats) {
            try {
                const parsed = parse(value.trim(), fmt, new Date())
                if (!isNaN(parsed)) {
                    return parsed.getHours() * 60 + parsed.getMinutes()
                }
            } catch (error) {
                // ignore
            }
        }
        const date = new Date(`1970-01-01T${value}`)
        if (!isNaN(date)) return date.getHours() * 60 + date.getMinutes()
        return null
    }

    const getDateInfoForSuggestion = React.useCallback(
        (suggestion) => {
            const parsedDate = parseSuggestionDate(suggestion)
            if (parsedDate) {
                return {
                    dateKey: format(parsedDate, 'yyyy-MM-dd'),
                    dayName: format(parsedDate, 'EEEE'),
                    parsedDate
                }
            }
            const dayNameRaw = suggestion?.eventOrTeacherObject?.day
            if (!dayNameRaw) return null
            const normalized = dayNameRaw.toLowerCase()
            const targetDate = weekDays.find((date) => {
                const longName = format(date, 'EEEE').toLowerCase()
                const shortName = format(date, 'EEE').toLowerCase()
                return (
                    longName === normalized ||
                    shortName === normalized ||
                    longName.startsWith(normalized)
                )
            })
            if (!targetDate) return null
            return {
                dateKey: format(targetDate, 'yyyy-MM-dd'),
                dayName: format(targetDate, 'EEEE'),
                parsedDate: targetDate
            }
        },
        [weekDays]
    )

    const findMatchingEvent = React.useCallback(
        (suggestion) => {
            const info = getDateInfoForSuggestion(suggestion)
            if (!info) return null
            const { dateKey, parsedDate } = info
            const dayEvents = weekEventsByDay[dateKey] || []
            if (!dayEvents.length) return null
            const obj = suggestion?.eventOrTeacherObject || {}
            const candidateIds = [
                obj.eventId,
                obj.id,
                obj._id,
                obj.calendarEventId,
                suggestion?.eventId
            ].filter(Boolean)
            let match = null
            if (candidateIds.length) {
                match = dayEvents.find((event) => candidateIds.includes(event.id))
            }
            const targetStart = (() => {
                if (parsedDate) return parsedDate.getHours() * 60 + parsedDate.getMinutes()
                if (obj.time) return parseTimeStringToMinutes(obj.time)
                if (Number.isFinite(obj.startMinutes)) return Number(obj.startMinutes)
                return null
            })()
            if (!match) {
                const normalizedFamily = obj.familyName?.toLowerCase()
                const normalizedStudent =
                    obj.name?.toLowerCase() || obj.studentName?.toLowerCase() || ''
                match = dayEvents.find((event) => {
                    const timeClose =
                        targetStart == null
                            ? true
                            : Math.abs(event.startMinutes - targetStart) <= 5
                    const familyMatch = normalizedFamily
                        ? event.familyName?.toLowerCase() === normalizedFamily
                        : true
                    const studentMatch = normalizedStudent
                        ? event.studentName?.toLowerCase().includes(normalizedStudent)
                        : true
                    return timeClose && (familyMatch || studentMatch)
                })
            }
            if (match) {
                return { dateKey, eventId: match.id }
            }
            return null
        },
        [weekEventsByDay, getDateInfoForSuggestion]
    )

    const driveSuggestionEventMap = React.useMemo(() => {
        const map = new Map()
        driveSuggestionEntries.forEach(({ suggestion, key }) => {
            const match = findMatchingEvent(suggestion)
            if (match) {
                map.set(`${match.dateKey}:${match.eventId}`, { suggestion, key })
            }
        })
        return map
    }, [driveSuggestionEntries, findMatchingEvent])

    const scrollDriveChipIntoView = React.useCallback((chipKey) => {
        const element = driveChipRefs.current.get(chipKey)
        if (element?.scrollIntoView) {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
    }, [])

    const activateDriveSuggestion = React.useCallback(
        (suggestion, key, options = {}) => {
            const { allowToggle = true, shouldScroll = false } = options
            if (allowToggle && activeDriveSuggestionKey === key) {
                setActiveDriveSuggestionKey(null)
                setPendingHighlightSuggestion(null)
                setHighlightedEvent(null)
                return
            }
            if (!allowToggle && activeDriveSuggestionKey === key) {
                if (shouldScroll) scrollDriveChipIntoView(key)
                setPendingHighlightSuggestion(suggestion)
                return
            }
            setActiveDriveSuggestionKey(key)
            const teacherId = getSuggestionTeacherId(suggestion)
            if (teacherId && teacherId !== selectedTeacherId) {
                setSelectedTeacherId(teacherId)
            }
            const info = getDateInfoForSuggestion(suggestion)
            if (info?.parsedDate) {
                const newAnchor = startOfWeek(info.parsedDate, { weekStartsOn: 0 })
                setWeekAnchor((prev) => (isSameDay(prev, newAnchor) ? prev : newAnchor))
            }
            setPendingHighlightSuggestion(suggestion)
            if (shouldScroll) scrollDriveChipIntoView(key)
        },
        [activeDriveSuggestionKey, selectedTeacherId, getDateInfoForSuggestion, scrollDriveChipIntoView]
    )

    const handleCalendarEventSelect = React.useCallback(
        (dateKey, eventId) => {
            const match = driveSuggestionEventMap.get(`${dateKey}:${eventId}`)
            if (!match) return
            activateDriveSuggestion(match.suggestion, match.key, {
                allowToggle: false,
                shouldScroll: true
            })
        },
        [driveSuggestionEventMap, activateDriveSuggestion]
    )

    const handleTeacherChipClick = (teacher) => {
        const teacherId = teacher?.TeacherID
        const filterKey = getTeacherFilterKey(teacher)
        if (teacherId) setSelectedTeacherId(teacherId)
        if (!filterKey) {
            setDriveFilterTeacherKey(null)
        } else {
            setDriveFilterTeacherKey((prev) => (prev === filterKey ? null : filterKey))
        }
    }

    const familyName = modalInfo?.todoName || modalInfo?.familyName || 'Unknown Family'
    const familyMessage =
        (modalInfo?.todoDescription && modalInfo.todoDescription.trim()) ||
        (modalInfo?.initialAvailabilityNotes && modalInfo.initialAvailabilityNotes.trim()) ||
        ''

    const filteredTeacherName = React.useMemo(() => {
        if (!driveFilterTeacherKey) return null
        const match = teacherPool.find((teacher) => getTeacherFilterKey(teacher) === driveFilterTeacherKey)
        const name = match?.Name?.trim()
        if (!name) return null
        const first = name.split(/\s+/)[0]
        return first || name
    }, [driveFilterTeacherKey, teacherPool])

    const selectedTeacherName = React.useMemo(() => {
        const match = teacherPool.find((teacher) => teacher.TeacherID === selectedTeacherId)
        const name = match?.Name?.trim()
        if (!name) return null
        const first = name.split(/\s+/)[0]
        return first || name
    }, [teacherPool, selectedTeacherId])

    React.useEffect(() => {
        if (!pendingHighlightSuggestion) return
        const suggestionTeacherId = getSuggestionTeacherId(pendingHighlightSuggestion)
        if (
            suggestionTeacherId &&
            selectedTeacherId &&
            suggestionTeacherId !== selectedTeacherId
        ) {
            return
        }
        const match = findMatchingEvent(pendingHighlightSuggestion)
        if (match) {
            setHighlightedEvent(match)
        } else {
            setHighlightedEvent(null)
        }
    }, [
        pendingHighlightSuggestion,
        weekEventsByDay,
        selectedTeacherId,
        findMatchingEvent,
        getDateInfoForSuggestion
    ])

    return (
        <div className='distance-scheduler-2025__backdrop'>
            <div className='distance-scheduler-2025__modal' ref={modalRef}>
                <div className='distance-scheduler-2025__header'>
                    <div className='distance-scheduler-2025__teacher-picker'>
                        {loadingTeachers && <div className='distance-scheduler-2025__loading'>Loading teachers…</div>}
                        {!loadingTeachers && teacherPool.length === 0 && (
                            <div className='distance-scheduler-2025__empty'>
                                {loadError || 'No teachers match the requested instruments yet.'}
                            </div>
                        )}
                        {!loadingTeachers && teacherPool.length > 0 && (
                            <>
                                {teacherPool.map((teacher) => {
                                    const filterKey = getTeacherFilterKey(teacher)
                                    const isFiltered = driveFilterTeacherKey && filterKey === driveFilterTeacherKey
                                    return (
                                        <button
                                            key={teacher.TeacherID}
                                            className={`distance-scheduler-2025__teacher-pill ${
                                                teacher.TeacherID === selectedTeacherId ? 'is-selected' : ''
                                            } ${isFiltered ? 'is-filtered' : ''}`}
                                            onClick={() => handleTeacherChipClick(teacher)}
                                            aria-pressed={teacher.TeacherID === selectedTeacherId}
                                        >
                                            {getTeacherImage(teacher) ? (
                                                <img
                                                    className='distance-scheduler-2025__teacher-pill-img'
                                                    src={getTeacherImage(teacher)}
                                                    alt={teacher.Name}
                                                />
                                            ) : (
                                                <span className='distance-scheduler-2025__teacher-pill-placeholder'>
                                                    {teacher.Name?.charAt(0) || 'T'}
                                                </span>
                                            )}
                                            <span className='distance-scheduler-2025__teacher-pill-label'>{teacher.Name}</span>
                                        </button>
                                    )
                                })}
                            </>
                        )}
                    </div>
                    <button className='distance-scheduler-2025__close' onClick={onClose}>
                        <MdClose size={20} />
                    </button>
                </div>

                <div className='distance-scheduler-2025__content'>
                    <aside className='distance-scheduler-2025__info-panel'>
                        <div className='distance-scheduler-2025__info-section'>
                            <p className='distance-scheduler-2025__label'>Family</p>
                            <h3 className='distance-scheduler-2025__info-title'>{familyName}</h3>
                            <p className='distance-scheduler-2025__info-subtitle'>
                                {schoolLabel} ·{' '}
                                {instrumentFilters.map((instrument) => instrument.label).join(', ') || 'No instruments on file'}
                            </p>
                            {familyMessage && (
                                <div className='distance-scheduler-2025__info-message'>{familyMessage}</div>
                            )}
                        </div>
                        {availabilityNote && (
                            <div className='distance-scheduler-2025__info-section distance-scheduler-2025__info-section--message'>
                                <p className='distance-scheduler-2025__label'>Scheduling Message</p>
                                <div className='distance-scheduler-2025__info-message'>
                                    {showFullAvailabilityNote || availabilityNote.length <= 85
                                        ? availabilityNote
                                        : createEllipses(availabilityNote, 85)}
                                </div>
                                {availabilityNote.length > 85 && (
                                    <button
                                        type='button'
                                        className='distance-scheduler-2025__info-toggle'
                                        onClick={() => setShowFullAvailabilityNote((prev) => !prev)}
                                    >
                                        Show {showFullAvailabilityNote ? 'less' : 'more'}
                                    </button>
                                )}
                            </div>
                        )}
                        {!!studentList.length && (
                            <div className='distance-scheduler-2025__info-section'>
                                <p className='distance-scheduler-2025__label'>Students</p>
                                <div className='distance-scheduler-2025__info-students'>
                                    {studentList.map((student) => {
                                        const familyStudent = getFamilyStudentMatch(student)
                                        const ageLabel = getStudentAgeLabel(student, familyStudent)
                                        const lessonLabel = getLessonLengthLabel(student, familyStudent)
                                        const detailSegments = []
                                        if (ageLabel) detailSegments.push(ageLabel)
                                        const instrumentSources = [
                                            student?.instrumentList,
                                            familyStudent?.instrumentList
                                        ].filter(Boolean)
                                        let instrumentLabel = ''
                                        for (const source of instrumentSources) {
                                            const label = source
                                                ?.map((instrument) => instrument.name || instrument.parentInstrument)
                                                .filter(Boolean)
                                                .join(', ')
                                            if (label) {
                                                instrumentLabel = label
                                                break
                                            }
                                        }
                                        if (instrumentLabel) detailSegments.push(instrumentLabel)
                                        const metaText = detailSegments.join(' · ')
                                        return (
                                        <div
                                            key={student.studentId || student.studentName}
                                            className='distance-scheduler-2025__header-student'
                                        >
                                            <div className='distance-scheduler-2025__header-student-name'>
                                                {student.studentName}
                                            </div>
                                            <div className='distance-scheduler-2025__header-student-meta'>
                                                {metaText || 'Details not available'}
                                            </div>
                                            {lessonLabel && (
                                                <div className='distance-scheduler-2025__header-student-extra'>
                                                    <span className='distance-scheduler-2025__header-student-pill'>
                                                        Lesson Length: {lessonLabel}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </aside>
                    <div className='distance-scheduler-2025__calendar-panel'>
                        <div className='distance-scheduler-2025__calendar-header'>
                            <button className='distance-scheduler-2025__nav-btn' onClick={() => moveWeek('prev')}>
                                <MdChevronLeft size={20} />
                            </button>
                            <div className='distance-scheduler-2025__range'>
                                <div className='distance-scheduler-2025__range-title'>{weekTitle}</div>
                                <div className='distance-scheduler-2025__range-label'>{formattedWeekLabel}</div>
                            </div>
                            <button className='distance-scheduler-2025__nav-btn' onClick={() => moveWeek('next')}>
                                <MdChevronRight size={20} />
                            </button>
                        </div>
                        {nylasAvailabilityLoading && (
                            <div className='distance-scheduler-2025__calendar-note'>
                                Syncing Google Calendar availability…
                            </div>
                        )}
                        {nylasAvailabilityError && !nylasAvailabilityLoading && (
                            nylasAvailabilityError.trim() === 'No connected personal calendar.' ? (
                                <div className='distance-scheduler-2025__calendar-note distance-scheduler-2025__calendar-note--error'>
                                    <span className='distance-scheduler-2025__calendar-sync-dot distance-scheduler-2025__calendar-sync-dot--error' aria-hidden='true' />
                                    {nylasAvailabilityError}
                                </div>
                            ) : (
                                <div className='distance-scheduler-2025__inline-error'>{nylasAvailabilityError}</div>
                            )
                        )}
                        {!nylasAvailabilityLoading && !nylasAvailabilityError && usingNylasAvailability && (
                            <div className='distance-scheduler-2025__calendar-note distance-scheduler-2025__calendar-note--synced'>
                                <span className='distance-scheduler-2025__calendar-sync-dot' aria-hidden='true' />
                                Google Calendar is Synced.
                            </div>
                        )}
                        {calendarLoading && (
                            <div className='distance-scheduler-2025__inline-note'>
                                {selectedTeacherName
                                    ? `Loading ${selectedTeacherName}'s Calendar…`
                                    : 'Loading calendar…'}
                            </div>
                        )}
                        {calendarError && !calendarLoading && (
                            <div className='distance-scheduler-2025__inline-error'>{calendarError}</div>
                        )}
                        <div className='distance-scheduler-2025__calendar-loading-overlay'>
                        {calendarLoading && (
                            <div className='distance-scheduler-2025__calendar-loading-overlay-inner'>
                                {selectedTeacherName
                                    ? `Loading ${selectedTeacherName}'s Calendar…`
                                    : 'Loading calendar…'}
                            </div>
                        )}
                        <div className='distance-scheduler-2025__calendar-body'>
                            <div className='distance-scheduler-2025__time-scale'>
                                {hourMarkers.map(({ label }, index) => (
                                    <span key={`${label}-${index}`}>{label}</span>
                                ))}
                            </div>
                            <div className='distance-scheduler-2025__week-grid'>
                                {weekDays.map((date) => {
                                    const dayName = DAY_NAMES[date.getDay()]
                                    const dateKey = format(date, 'yyyy-MM-dd')
                                    return (
                                        <div key={dayName} className='distance-scheduler-2025__day-column'>
                                            <div className='distance-scheduler-2025__day-header'>
                                                <span className='distance-scheduler-2025__day-name'>{format(date, 'EEE')}</span>
                                                <span className='distance-scheduler-2025__day-date'>{format(date, 'MMM d')}</span>
                                            </div>
                                            <div className='distance-scheduler-2025__day-grid'>
                                                {hourMarkers.map(({ position }, index) => (
                                                    <div
                                                        key={`${dayName}-hour-line-${index}`}
                                                        className='distance-scheduler-2025__hour-line'
                                                        style={{ top: `${position}%` }}
                                                    />
                                                ))}
                                                {renderAvailabilityBlocks(dayName, dateKey)}
                                                {renderBusyBlocks(dayName, dateKey)}
                                                {renderLessonBlocks(dateKey)}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                        </div>
                    </div>
                    <aside className='distance-scheduler-2025__sidebar'>
                        <h3 className='distance-scheduler-2025__sidebar-title'>
                            {filteredTeacherName ? `${filteredTeacherName}'s Drive Times` : 'Drive Times'}
                        </h3>
                        <div className='distance-scheduler-2025__drive-list-wrapper'>
                            {driveSuggestionEntries.length === 0 ? (
                                <p className='distance-scheduler-2025__sidebar-hint'>
                                    No drive time suggestions yet. Run the scheduling suggestions to populate this list.
                                </p>
                            ) : visibleDriveSuggestions.length === 0 ? (
                                <p className='distance-scheduler-2025__sidebar-hint'>
                                    No drive times for the selected teacher filter.
                                </p>
                            ) : (
                                <div className='distance-scheduler-2025__drive-chip-list'>
                                    {visibleDriveSuggestions.map(({ suggestion, key: chipKey }) => {
                                        const isActive = activeDriveSuggestionKey === chipKey
                                        return (
                                            <div
                                                key={chipKey}
                                                role='button'
                                                tabIndex={0}
                                                className={`distance-scheduler-2025__drive-chip-wrapper ${
                                                    isActive ? 'is-active' : ''
                                                }`}
                                                ref={(node) => {
                                                    if (node) {
                                                        driveChipRefs.current.set(chipKey, node)
                                                    } else {
                                                        driveChipRefs.current.delete(chipKey)
                                                    }
                                                }}
                                                onClick={() =>
                                                    activateDriveSuggestion(suggestion, chipKey, {
                                                        allowToggle: true,
                                                        shouldScroll: true
                                                    })
                                                }
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault()
                                                        activateDriveSuggestion(suggestion, chipKey, {
                                                            allowToggle: true,
                                                            shouldScroll: true
                                                        })
                                                    }
                                                }}
                                            >
                                                <TeacherDriveTimeBubble
                                                    suggestion={suggestion}
                                                    invitations={[]}
                                                    setSchedulingHelperTeacherId={(id) =>
                                                        id && setSelectedTeacherId(id)
                                                    }
                                                />
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    </aside>
                </div>
                {hoveredLesson && (
                    <div
                        className='distance-scheduler-2025__tooltip'
                        style={{ top: hoveredLesson.top, left: hoveredLesson.left }}
                    >
                        <div className='distance-scheduler-2025__tooltip-label'>{hoveredLesson.lesson.studentLabel}</div>
                        {hoveredLesson.lesson.familyLabel && (
                            <div className='distance-scheduler-2025__tooltip-sub'>{hoveredLesson.lesson.familyLabel}</div>
                        )}
                        <div className='distance-scheduler-2025__tooltip-time'>{hoveredLesson.lesson.timeLabel}</div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default DistanceSchedulerFall2025
